import { useCallback, useMemo, useState } from 'react'
import type { Document, Manifest } from '../api/authoring'
import { payloadId, shown } from '../api/useIntent'
import { fieldTarget, placementTarget } from '../model/command'
import {
  composeDocument,
  regionGroup,
  type CompositionNode,
  type CompositionRegion,
  type RegionRef,
} from '../model/composition'
import { allLinkIds, mintLinkId, encodeLinkPath, withinPath, type LinkPath } from '../model/link'
import { containable, intentArranging, structuralPayload, typeTitle } from '../model/manifest'
import { Composition, type Arranging } from '../screens/page/Composition'
import { PlacementPicker } from '../screens/page/PlacementPicker'
import { ComponentName } from './ComponentName'
import { PinDialog } from './PinDialog'
import { ExtractDialog } from './ExtractDialog'
import { useReorder } from './reorder'

/**
 * Arranging one document's children — every node in it, not only the
 * root's.
 *
 * **Shared by the two screens that hold a document**, because arranging
 * is a property of the document rather than of the screen: a page editor
 * and a standalone component's editor were about to grow the same three
 * callbacks against the same manifest, and two implementations of "which
 * intent places here" is exactly the drift the derivation exists to
 * prevent.
 *
 * What it does not own is *selection* — which node the surrounding screen
 * is editing. The page editor keeps that in the URL so a colleague can be
 * sent a link to one placement; a component editor may not have the
 * concept at all. So it is passed in.
 */
export function CompositionEditor({
  tenant,
  document,
  manifest,
  selection,
  onSelect,
  send,
  hides,
  busy,
}: {
  tenant: string
  document: Document
  manifest: Manifest | undefined
  selection: LinkPath
  onSelect: (path: LinkPath) => void
  send: (id: string, intent: string, payload: Record<string, unknown>, path?: LinkPath) => Promise<unknown>
  /** Arrangements the surrounding screen draws itself, by property name. */
  hides?: string[]
  busy: boolean
}) {
  // Which node's region the picker is open over. A (node, region) pair
  // rather than a region name, because two nodes on one screen may both
  // have a region called `body` and a name alone cannot tell them apart.
  const [addingTo, setAddingTo] = useState<{ node: CompositionNode; region: CompositionRegion } | null>(null)
  const [pinning, setPinning] = useState<CompositionNode | null>(null)
  const [extracting, setExtracting] = useState<CompositionNode | null>(null)

  // The whole document as a tree of nodes and regions, read from each
  // type's own fields schema. The root is this component; every inline
  // node below it is a node on the same terms.
  const root = useMemo(() => composeDocument(manifest, document), [manifest, document])

  // What may be done to a node's region, resolved per node rather than
  // once for the screen: a Page's regions are served by
  // `page.block_placed` and a Container's by `container.item_placed`, and
  // both are on one screen at the same time.
  //
  // `region.keyed` picks between a type's slot-carrying intents and its
  // others, which is the same fact that produced the region — so "which
  // intent places here" and "is this a named region" cannot disagree.
  const intentsFor = useCallback(
    (node: CompositionNode, region: CompositionRegion) => ({
      place: intentArranging(manifest, node.type, 'place', region.keyed),
      act: intentArranging(manifest, node.type, 'act', region.keyed),
      arrange: intentArranging(manifest, node.type, 'arrange', region.keyed),
      // A move always carries the region it is joining, so it is one of
      // the type's slot-carrying intents whichever region the drag
      // started in.
      move: intentArranging(manifest, node.type, 'move', true),
    }),
    [manifest],
  )

  // The words, taken from the intent each role resolved to rather than
  // written here. A type that declared its own says them — a Gallery
  // offers "Add a photo" — and one that did not falls back to what the
  // intent's name derives to, which is what `title` on a resolved
  // structural intent already carries. Either way this control names no
  // type and no verb, which is the whole reason the markers stop short
  // of one.
  // What each type is called, resolved once for the tree rather than by
  // every row: the composition control draws a node's type as its
  // primary text, and a wire name there is the model showing through.
  const titleOf = useCallback((typeName: string) => typeTitle(manifest, typeName), [manifest])

  // What a linked placement points at, in the target's own words. The
  // tree cannot read another stream and should not learn how, so it is
  // handed an element per row — the same shape the content-mode tree
  // takes its editor as, and for the same reason.
  //
  // A pinned placement is named at the revision it resolves to, not at
  // the head: a row reading a headline the visitor is not being shown
  // would be a lie about the one thing a pin exists to guarantee.
  const renderTarget = useCallback(
    (placement: CompositionNode) =>
      placement.targetId ? (
        <ComponentName
          tenant={tenant}
          id={placement.targetId}
          revision={placement.pin?.kind === 'pinned' ? placement.pin.revision : undefined}
          manifest={manifest}
          prefix="→ "
        />
      ) : null,
    [manifest, tenant],
  )

  const arrangingFor = useCallback(
    (node: CompositionNode, region: CompositionRegion): Arranging => {
      const intents = intentsFor(node, region)
      return {
        place: intents.place && { label: intents.place.title },
        act: intents.act && { label: intents.act.title },
        arrange: intents.arrange && { label: intents.arrange.title },
        // Only where the region is one of several named ones: a move
        // carries both ends, and a type keeping one unnamed list has
        // nowhere to move a placement to.
        move: region.keyed && intents.move ? { label: intents.move.title } : undefined,
      }
    },
    [intentsFor],
  )

  // Where a command about a node's *own* arrangement goes. An inline node
  // has no stream, so it is this component carrying the path — which is
  // why it is `fieldTarget` and not `placementTarget`: the node holding
  // the placement is the node being addressed, not its parent.
  const holderOf = useCallback(
    (node: CompositionNode) => fieldTarget(document, node.path),
    [document],
  )

  const place = useCallback(
    async (node: CompositionNode, region: CompositionRegion, child: Record<string, unknown>) => {
      const intent = intentsFor(node, region).place
      const holder = holderOf(node)
      if (!intent || !holder || holder.crossedLink) return
      await send(
        holder.id,
        intent.name,
        structuralPayload(intent, {
          child,
          slot: region.slot,
          // The stored length, dangling entries included, so a new
          // placement lands after everything the arrangement already
          // names rather than before whatever has lost its child.
          position: region.linkIds.length,
        }),
        holder.path,
      )
      setAddingTo(null)
    },
    [holderOf, intentsFor, send],
  )

  const reorder = useCallback(
    async (node: CompositionNode, region: CompositionRegion, order: string[]) => {
      const intent = intentsFor(node, region).arrange
      const holder = holderOf(node)
      if (!intent || !holder || holder.crossedLink) return
      // The complete order, always. A reorder is a rearrangement: one
      // that omits a placement *keeps* it, so a short order can never be
      // used to remove — and sending the whole thing means the intent
      // says exactly what the author sees. The control computes it,
      // because a drop and a menu step land the same array here.
      await send(holder.id, intent.name, structuralPayload(intent, { slot: region.slot, order }), holder.path)
    },
    [holderOf, intentsFor, send],
  )

  // Whether a block may be dragged out of one region and into another.
  //
  // **Only within one node, and only where the type declares a move.**
  // The fold rearranges one node's map of regions, so there is nothing
  // that could carry a block from a page's body into a section's list —
  // that is a remove and a place, two revisions with the block off the
  // page in between. A type that declares no move intent simply does not
  // accept the drop, rather than accepting it and doing half of it.
  const canMove = useCallback(
    (from: { meta: RegionRef }, to: { meta: RegionRef }) =>
      encodeLinkPath(from.meta.node.path) === encodeLinkPath(to.meta.node.path) &&
      from.meta.region.keyed &&
      to.meta.region.keyed &&
      Boolean(intentsFor(to.meta.node, to.meta.region).move),
    [intentsFor],
  )

  const moveBetween = useCallback(
    async (from: RegionRef, to: RegionRef, linkId: string, position: number) => {
      const intent = intentsFor(to.node, to.region).move
      const holder = holderOf(to.node)
      if (!intent || !holder || holder.crossedLink) return
      // Both ends and the index, in one command. The block itself is
      // never restated: it stays a child of this node, and the fold
      // moves the link id between the two lists that name it.
      await send(
        holder.id,
        intent.name,
        structuralPayload(intent, {
          link: linkId,
          fromSlot: from.region.slot,
          slot: to.region.slot,
          position,
        }),
        holder.path,
      )
    },
    [holderOf, intentsFor, send],
  )

  // One hook for the whole tree, because a drop that crosses two regions
  // can only be seen by something watching both.
  const dragging = useReorder<RegionRef>(
    (_group, order, meta) => {
      if (meta) reorder(meta.node, meta.region, order).catch(shown)
    },
    !busy,
    {
      allow: canMove,
      send: (move) => moveBetween(move.from.meta, move.to.meta, move.id, move.position).catch(shown),
    },
  )

  const act = useCallback(
    async (node: CompositionNode, region: CompositionRegion, placement: CompositionNode) => {
      const intent = intentsFor(node, region).act
      const holder = holderOf(node)
      if (!intent || !holder || holder.crossedLink || !placement.linkId) return
      await send(
        holder.id,
        intent.name,
        structuralPayload(intent, { slot: region.slot, link: placement.linkId }),
        holder.path,
      )
      // The selection may have been *inside* what just went, not only on
      // it — removing a Container takes every node under it.
      if (withinPath(selection, placement.path)) onSelect([])
    },
    [holderOf, intentsFor, onSelect, selection, send],
  )

  const placeChild = useCallback(
    (child: Record<string, unknown>) => {
      if (!addingTo) return
      place(addingTo.node, addingTo.region, child).catch(shown)
    },
    [addingTo, place],
  )

  return (
    <>
      <Composition
        root={root}
        arrangingFor={arrangingFor}
        titleOf={titleOf}
        dragging={dragging}
        groupOf={regionGroup}
        renderTarget={renderTarget}
        selection={selection}
        actions={{
          onSelect,
          onAdd: (node, region) => setAddingTo({ node, region }),
          onReorder: (node, region, order) => reorder(node, region, order).catch(shown),
          // At the end of the region it joins, which is where a drop on an
          // empty region lands it too.
          onMove: (node, from, to, placement) =>
            moveBetween({ node, region: from }, { node, region: to }, placement.linkId!, to.linkIds.length).catch(shown),
          onAct: (node, region, placement) => act(node, region, placement).catch(shown),
          onPin: (node) => node.pin?.kind === 'inline' ? setExtracting(node) : setPinning(node),
        }}
        hides={hides}
        busy={busy}
      />

      {/* The palette is the *holding node's*, not the root's — a
          Container may hold a different set from the page it sits in,
          and the manifest is what says so. */}
      <PlacementPicker
        tenant={tenant}
        into={addingTo ? (addingTo.region.slot ?? titleOf(addingTo.node.type).toLowerCase()) : null}
        palette={addingTo ? containable(manifest, addingTo.node.type) : []}
        manifest={manifest}
        onClose={() => setAddingTo(null)}
        onLink={(targetId) => {
          const taken = new Set(allLinkIds(document.children))
          // A linked child names a target and **states no type** — the
          // surface reads it off the target and refuses a stated one. The
          // id is composite because it sits inside an untyped payload.
          placeChild({ linkId: mintLinkId(targetId, taken), id: payloadId(tenant, targetId) })
        }}
        onInline={(typeName, fields) => {
          const taken = new Set(allLinkIds(document.children))
          // An inline child **states its own type**, because it is being
          // defined by this request and there is nothing to read it from.
          // The mirror image of the rule above, and the reason `Child`
          // and `ChildInput` are two schemas. It states its required
          // fields for the same reason: the placement is this node's
          // first revision, so a type whose document cannot be empty has
          // nowhere else to carry them.
          placeChild({ linkId: mintLinkId(typeName.toLowerCase(), taken), type: typeName, fields })
        }}
        busy={busy}
      />

      {/* A pin is arranged by the node that **holds** the placement, so
          `placementTarget` resolves the holder rather than the target —
          pinning a Content nested in an inline Grid is a command against
          this document carrying the path to the Grid, not against the
          Content, which knows nothing about where it sits. */}
      {pinning?.targetId &&
        pinning.linkId &&
        (() => {
          const holder = placementTarget(document, pinning.path)
          if (!holder || holder.crossedLink) return null
          return (
            <PinDialog
              tenant={tenant}
              open
              commandId={holder.id}
              commandPath={holder.path}
              edge={{ kind: 'child', linkId: pinning.linkId }}
              targetId={pinning.targetId}
              pin={pinning.pin!}
              onClose={() => setPinning(null)}
            />
          )
        })()}

      <ExtractDialog
        tenant={tenant}
        document={document}
        node={extracting}
        title={extracting ? titleOf(extracting.type) : 'component'}
        onClose={() => setExtracting(null)}
        send={send}
      />
    </>
  )
}

